import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetchJSON } from '../apiFetch';
import ProtectedPilotSourcesPanel from '../components/ProtectedPilotSourcesPanel';
import ProtectedPilotExecutionPanel from '../components/ProtectedPilotExecutionPanel';
import ProtectedSectionProductionObservationPanel from '../components/ProtectedSectionProductionObservationPanel';

const STATUS_LABELS = {
  NORMAL: 'Normal',
  FALLBACK_ACTIVE: 'Fallback ativo',
  BACKEND_LOCUTOR_STALE: 'Locutor backend atrasado',
  FALLBACK_CACHE_EMPTY: 'Cache de fallback vazio',
  PLAYER_OFFLINE: 'Player offline',
  PLAYER_SILENT: 'Player sem audio',
  LOCUTOR_PENDING: 'Locutor pendente',
  AUDIO_READY_NOT_SENT: 'Audio pronto sem entrega',
  TEXT_DEGRADED: 'Texto degradado',
  TTS_DEGRADED: 'TTS degradado',
  UNKNOWN: 'Sem dados',
  NO_DETERMINISTIC_EXECUTION_OBSERVED: 'Sem execução M1 observada',
  M1_FACTUAL_COMPOSED_NOT_DELIVERED: 'M1 composto, entrega não confirmada',
  M1_FACTUAL_DELIVERED_TO_PLAYER: 'M1 entregue ao player',
  M1_FACTUAL_PLAYBACK_CONFIRMED: 'M1 reproduzido e encerrado',
};

const UPDATE_PHASE_LABELS = {
  idle: 'Aguardando verificação',
  checking: 'Verificando',
  up_to_date: 'Atualizado',
  available: 'Atualização disponível',
  downloading: 'Baixando',
  downloaded: 'Baixado',
  installing: 'Instalando',
  error: 'Falha',
};

const TONE = {
  ok: { bg: 'rgba(var(--success-rgb), 0.12)', border: 'rgba(var(--success-rgb), 0.45)', text: 'var(--success)' },
  warning: { bg: 'rgba(var(--warning-rgb), 0.14)', border: 'rgba(var(--warning-rgb), 0.45)', text: 'var(--warning)' },
  critical: { bg: 'rgba(var(--error-rgb), 0.14)', border: 'rgba(var(--error-rgb), 0.45)', text: 'var(--error)' },
  unknown: { bg: 'rgba(var(--neutral-300-rgb), 0.16)', border: 'var(--border)', text: 'var(--text-secondary)' },
};

function getClienteId() {
  try {
    return JSON.parse(localStorage.getItem('radio-ai-user') || '{}').id || '';
  } catch {
    return '';
  }
}

function fmtAge(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '-';
  const value = Math.max(0, Math.floor(Number(seconds)));
  if (value < 60) return `${value}s`;
  const minutes = Math.floor(value / 60);
  const rest = value % 60;
  if (minutes < 60) return rest ? `${minutes}min ${rest}s` : `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const min = minutes % 60;
  return min ? `${hours}h ${min}min` : `${hours}h`;
}

function fmtDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
}

function fmtBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '-';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Metric({ label, value }) {
  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      padding: '9px 0',
      display: 'grid',
      gridTemplateColumns: 'minmax(130px, 0.8fr) minmax(0, 1.2fr)',
      gap: 12,
      alignItems: 'baseline',
    }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{
      border: '1px solid var(--border)',
      background: 'var(--bg-surface)',
      padding: 16,
      minWidth: 0,
    }}>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: 15,
        fontWeight: 700,
        color: 'var(--text-primary)',
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function LocutorMonitorScreen() {
  const clienteId = useMemo(() => getClienteId(), []);
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const carregar = useCallback(async () => {
    if (!clienteId) {
      setError('Cliente não identificado.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await apiFetchJSON(`/api/locutor-monitor/status/${clienteId}`);
      setMonitor(data.monitor || null);
    } catch (err) {
      setError(err.message || 'Falha ao carregar monitor.');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    carregar();
    const interval = setInterval(carregar, 10000);
    return () => clearInterval(interval);
  }, [carregar]);

  const tone = TONE[monitor?.severity || 'unknown'] || TONE.unknown;
  const statusLabel = STATUS_LABELS[monitor?.status] || monitor?.status || 'Sem dados';

  return (
    <div style={{ width: '100%', maxWidth: 1180, margin: '0 auto', paddingTop: 18 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 14,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: 'var(--text-primary)' }}>Monitor Locutor</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
            Atualizado em {fmtDate(monitor?.checkedAt)}
          </div>
        </div>
        <button
          type="button"
          onClick={carregar}
          disabled={loading}
          style={{
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            padding: '9px 13px',
            borderRadius: 6,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          Atualizar
        </button>
      </div>

      <div style={{
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        padding: 14,
        marginBottom: 14,
        display: 'grid',
        gridTemplateColumns: 'minmax(180px, 0.4fr) minmax(0, 1fr)',
        gap: 14,
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Estado operacional</div>
          <div style={{ color: tone.text, fontSize: 20, fontWeight: 800 }}>{statusLabel}</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {monitor?.action || 'Aguardando dados do player.'}
        </div>
      </div>

      {error && (
        <div style={{
          border: '1px solid rgba(var(--error-rgb), 0.4)',
          background: 'rgba(var(--error-rgb), 0.12)',
          color: 'var(--error)',
          padding: 12,
          marginBottom: 14,
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <ProtectedSectionProductionObservationPanel clienteId={clienteId} observation={monitor?.protectedProduction} refreshFailed={Boolean(error)} />
      <ProtectedPilotSourcesPanel clienteId={clienteId} />
      <ProtectedPilotExecutionPanel clienteId={clienteId} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 14,
      }}>
        <Section title="Player">
          <Metric label="Conectado" value={monitor?.player?.connected ? 'Sim' : 'Não'} />
          <Metric label="Versão" value={monitor?.player?.appVersion || '-'} />
          <Metric label="Último status" value={fmtAge(monitor?.player?.lastStatusAgeSec)} />
          <Metric label="Faixa" value={monitor?.player?.track || '-'} />
          <Metric label="Tocando" value={monitor?.player?.playing ? 'Sim' : 'Não'} />
        </Section>

        <Section title="Atualização do EXE">
          <Metric
            label="Estado"
            value={UPDATE_PHASE_LABELS[monitor?.update?.phase] || monitor?.update?.phase || 'Sem telemetria'}
          />
          <Metric label="Versão atual" value={monitor?.update?.currentVersion || monitor?.player?.appVersion || '-'} />
          <Metric label="Versão disponível" value={monitor?.update?.availableVersion || '-'} />
          <Metric label="Versão baixada" value={monitor?.update?.downloadedVersion || '-'} />
          <Metric
            label="Progresso"
            value={monitor?.update?.downloadPercent !== null
              && monitor?.update?.downloadPercent !== undefined
              && Number.isFinite(Number(monitor.update.downloadPercent))
              ? `${Number(monitor.update.downloadPercent).toFixed(1)}%`
              : '-'}
          />
          <Metric label="Última verificação" value={fmtDate(monitor?.update?.lastCheckAt)} />
          <Metric label="Resultado da verificação" value={monitor?.update?.lastCheckResult || '-'} />
          <Metric label="Instalação agendada" value={fmtDate(monitor?.update?.installScheduledAt)} />
          <Metric label="Prazo forçado" value={fmtDate(monitor?.update?.forceInstallAfter)} />
          <Metric label="Último bloqueio" value={monitor?.update?.lastBlockedReason || '-'} />
          <Metric label="Última tentativa" value={fmtDate(monitor?.update?.lastInstallAttemptAt)} />
          <Metric label="Resultado da instalação" value={monitor?.update?.lastInstallResult || '-'} />
          <Metric label="Código do erro" value={monitor?.update?.lastErrorCode || '-'} />
          <Metric label="Erro" value={monitor?.update?.lastError || '-'} />
          <Metric label="Requer atenção" value={monitor?.update?.needsAttention ? 'Sim' : 'Não'} />
        </Section>

        <Section title="Fallback">
          <Metric label="Ativo" value={monitor?.fallback?.active ? 'Sim' : 'Não'} />
          <Metric label="Tempo ativo" value={fmtAge(monitor?.fallback?.ageSec)} />
          <Metric label="Cache lógico" value={monitor?.fallback?.cacheSize ?? '-'} />
          <Metric label="Arquivos locais" value={monitor?.fallback?.bufferSize ?? '-'} />
          <Metric label="Tamanho local" value={fmtBytes(monitor?.fallback?.bufferBytes)} />
          <Metric label="Execuções" value={monitor?.fallback?.playCount ?? '-'} />
          <Metric label="Cache desativado" value={monitor?.fallback?.cacheDisabled ? 'Sim' : 'Não'} />
        </Section>

        <Section title="Backend">
          <Metric label="Última locução gerada pelo backend" value={fmtDate(monitor?.backend?.lastBackendLocutorAt)} />
          <Metric label="Idade" value={fmtAge(monitor?.backend?.backendLocutorAgeSec)} />
          <Metric label="Último término" value={fmtDate(monitor?.backend?.lastBackendLocutorEndedAt)} />
          <Metric label="Último cache update" value={fmtDate(monitor?.backend?.lastCacheUpdateAt)} />
          <Metric label="Motivo cache" value={monitor?.backend?.lastCacheUpdateReason || '-'} />
          <Metric label="Áudio pronto" value={fmtAge(monitor?.backend?.lastAudioReadyAgeSec)} />
        </Section>

        <Section title="Execução factual M1">
          <Metric
            label="Estado"
            value={STATUS_LABELS[monitor?.factualM1?.status] || monitor?.factualM1?.status || 'Sem dados'}
          />
          <Metric label="Degrau" value={monitor?.factualM1?.degrau || '-'} />
          <Metric label="Seção" value={monitor?.factualM1?.sectionName || '-'} />
          <Metric label="Servida em" value={fmtDate(monitor?.factualM1?.servedAt)} />
          <Metric label="Entregue ao player" value={monitor?.factualM1?.deliveredToPlayer ? 'Sim' : 'Não confirmado'} />
          <Metric label="Término confirmado" value={monitor?.factualM1?.playerEnded ? 'Sim' : 'Não'} />
          <Metric label="Provider textual" value={monitor?.factualM1?.providerCalled ? 'Chamado' : 'Não chamado'} />
          <Metric label="Trace" value={monitor?.factualM1?.traceId || '-'} />
        </Section>

        <Section title="Watchdog">
          <Metric label="Último check" value={fmtAge(monitor?.watchdog?.lastCheckAgeSec)} />
          <Metric label="Último bloqueio" value={fmtAge(monitor?.watchdog?.lastBlockedAgeSec)} />
          <Metric label="Áudio sem entrega" value={monitor?.watchdog?.audioReadyWithoutPlay ? 'Sim' : 'Não'} />
          <Metric label="Play sem fim" value={monitor?.watchdog?.playWithoutEnded ? 'Sim' : 'Não'} />
          <Metric label="Trace" value={monitor?.watchdog?.traceId || '-'} />
        </Section>
      </div>
    </div>
  );
}
